// The results table: row markup, display order and the figures derived from
// a row. Pure functions over a stage1 response (results.test.ts).
import { escapeHtml, money } from '../format';
import type { ResultRow } from '../types';

const NATIVE_SYMBOLS: Record<string, string> = { EUR: '€', USD: '$', GBP: '£' };
const TAG_LABELS: Record<string, [string, string]> = {
  lowest_total_cost: ['Lowest total cost', 'tag tag-best'],
  lowest_rate: ['Lowest rate', 'tag tag-outline'],
  regulated: ['Regulated', 'tag tag-neutral'],
};
export const RESULTS_PREVIEW_COUNT = 5;

export type SortBy = 'totalCost' | 'rate' | 'maxLoan';

/**
 * The most this lender could lend on this property: their loan cap (if any),
 * also limited by their max LTV on the property value entered. Used by the
 * "maximum loan" sort and the Gross loan column.
 */
/** existingChargesGbp: a 2nd charge's LTV limit covers the existing mortgage(s) too, so they come off it. */
export function achievableMaxLoanGbp(r: ResultRow, propertyValueGbp: number, existingChargesGbp = 0): number {
  const ltvCappedLoanGbp = Math.max(0, propertyValueGbp * (r.maxLtvPct / 100) - (r.combinedLtvPct != null ? existingChargesGbp : 0));
  return r.noMaxLoanAvailable || r.maxLoanGbp === null ? ltvCappedLoanGbp : Math.min(r.maxLoanGbp, ltvCappedLoanGbp);
}

/**
 * Display order. 'totalCost' keeps the server's order (already cheapest
 * first, rate-on-application products last).
 */
export function sortResults(results: ResultRow[], sortBy: SortBy, propertyValueGbp: number, existingChargesGbp = 0): ResultRow[] {
  const arr = results.slice();
  if (sortBy === 'rate') {
    arr.sort((a, b) => (a.isRoa !== b.isRoa ? (a.isRoa ? 1 : -1) : a.rateMonthlyPct - b.rateMonthlyPct));
  } else if (sortBy === 'maxLoan') {
    arr.sort((a, b) => achievableMaxLoanGbp(b, propertyValueGbp, existingChargesGbp) - achievableMaxLoanGbp(a, propertyValueGbp, existingChargesGbp));
  }
  return arr;
}

/** The cheapest total cost (to the pound, as shown) across priced products; every product tied on it is marked "Lowest total cost". */
export function lowestTotalCostGbp(results: ResultRow[]): number | null {
  const costs = results.filter((r) => !r.isRoa).map((r) => Math.round(r.totalCostGbp));
  return costs.length > 0 ? Math.min(...costs) : null;
}

export function resultsHeaderHtml(showGrossLoan: boolean): string {
  const tip = (text: string, about: string) => `<button type="button" class="info-tip" data-tip="${escapeHtml(text)}" aria-label="More about ${about}">?</button>`;
  return `
      <span>Lender / Product</span>
      <span>Rate pm${tip('This is the estimated interest rate, at the next stage we will confirm this and may be able to negotiate a reduced rate.', 'rate pm')}</span>
      <span>Monthly interest${tip('This is an estimate based on the information provided that assumes the arrangement fee is added to the loan. An accurate figure will be provided at the next step by an adviser', 'monthly interest')}</span>
      <span>Max LTV${tip('Maximum loan to value. The total amount you can borrow will depend on a number of factors, including how the bridging loan is serviced.', 'max LTV')}</span>
      ${showGrossLoan ? `<span>Gross loan${tip('The maximum loan we estimate this lender could offer against the property value entered, based on their loan-to-value limit and any absolute loan cap.', 'gross loan')}</span>` : ''}
      <span>Arrangement fee${tip('This is the main product fee, which is often a percentage of the loan amount. Other fees will often apply, including admin, valuation, legal and exit fees. These will be shared with you at the next stage by your adviser', 'arrangement fee')}</span>
      <span>Approx total cost${tip('This estimate covers your main borrowing costs, including interest and the arrangement fee. An adviser will confirm any remaining fees at the next step. Be aware that some lenders take their fees directly out of your initial loan payout rather than adding them to your loan total.', 'approx total cost')}</span>
      <span></span>`;
}

export interface RowContext {
  loanGbp: number;
  propertyValueGbp: number;
  /** 2nd charge: what's outstanding on the existing mortgage(s). */
  existingChargesGbp?: number;
  showGrossLoan: boolean;
  rowIndex: number;
  isBest: boolean;
  brandName: string;
}

export function resultRowHtml(r: ResultRow, ctx: RowContext): string {
  const { loanGbp, showGrossLoan, rowIndex, isBest } = ctx;
  // "Lowest total cost" is decided here (see lowestTotalCostGbp), so ties all get it.
  const tagInfo = r.tag && r.tag !== 'lowest_total_cost' ? TAG_LABELS[r.tag] : null;
  const chargeLabel = r.chargeType === 'second_charge' ? 'Second charge' : 'First charge';
  const regulatedLabel = r.regulatedStatus === 'regulated' ? 'Regulated' : 'Unregulated';
  // A range with no upper figure shows as "from X%".
  const isFromOnly = r.isRateRange && r.rateMonthlyPctTo === null && !r.isRoa;
  const rateDisplay = r.isRoa
    ? 'ROA'
    : isFromOnly
      ? `from ${r.rateMonthlyPct.toFixed(2)}%`
      : r.isRateRange && r.rateMonthlyPctTo !== null
        ? `${r.rateMonthlyPct.toFixed(2)}%–${r.rateMonthlyPctTo.toFixed(2)}%`
        : `${r.rateMonthlyPct.toFixed(2)}%`;
  const interestDisplay = r.isRoa
    ? 'On application'
    : r.isRateRange && r.rateMonthlyPctTo !== null
      ? `${money(loanGbp * r.rateMonthlyPct / 100)}–${money(loanGbp * r.rateMonthlyPctTo / 100)}`
      : money(loanGbp * r.rateMonthlyPct / 100);
  const costDisplay = r.isRoa
    ? 'Costs on application'
    : r.isRateRange && r.totalCostGbpTo !== null
      ? `${money(r.totalCostGbp)}–${money(r.totalCostGbpTo)}`
      : money(r.totalCostGbp);
  // A monthly rate worked out from an annual one is marked "approx", with
  // the annual rate shown for reference (not for ROA rows).
  const showDerivedRate = r.isDerivedRate && !r.isRoa;
  const annualRangeText = r.annualRatePctTo !== null
    ? `${r.annualRatePct.toFixed(2)}%–${r.annualRatePctTo.toFixed(2)}%`
    : r.isRateRange
      ? `from ${r.annualRatePct.toFixed(2)}%`
      : `${r.annualRatePct.toFixed(2)}%`;
  const annualRateLine = !showDerivedRate ? '' : r.rateBasis === 'above_boe_base'
    ? `Annual rate: ${annualRangeText} + BoE (${r.boeBaseRatePctUsed !== null ? r.boeBaseRatePctUsed.toFixed(2) + '%' : '—'})`
    : `Annual rate: ${annualRangeText}`;
  // Lenders pricing in another currency: each £ figure also shows their own
  // currency underneath, at the rate the quote used (fxRate, units per £1).
  const isForeign = !!(r.currency && r.currency !== 'GBP' && r.fxRate);
  const nativeAmount = (gbp: number) => (NATIVE_SYMBOLS[r.currency] || r.currency + ' ') + Math.round(gbp * (r.fxRate as number)).toLocaleString('en-GB');
  const nativeLine = (gbp: number, gbpTo?: number | null) => !isForeign || !gbp ? '' : `<span class="native-amount">${nativeAmount(gbp)}${gbpTo !== undefined && gbpTo !== null ? '–' + nativeAmount(gbpTo) : ''}</span>`;
  const grossLoan = achievableMaxLoanGbp(r, ctx.propertyValueGbp, ctx.existingChargesGbp ?? 0);
  const grossLoanCellHtml = !showGrossLoan ? '' : `
          <div class="cell"><span class="cell-label">Gross loan</span><span class="cell-value" style="font-weight:700">${money(grossLoan)}${nativeLine(grossLoan)}</span></div>`;
  const fromMini = isFromOnly ? '<span class="mini">from</span>' : '';
  const detailsId = `result-details-${rowIndex}`;
  const badges = [...(r.lenderTags || []), ...(r.productTags || [])];
  const code = escapeHtml(r.code);
  return `
      <li class="result${isBest ? ' is-best' : ''}${r.isExclusive ? ' is-exclusive' : ''}">
        <div class="result-grid">
          <div class="cell cell-lender">
            <div class="badges">${isBest ? `<span class="${TAG_LABELS.lowest_total_cost[1]}">${TAG_LABELS.lowest_total_cost[0]}</span>` : ''}${tagInfo ? `<span class="${tagInfo[1]}">${tagInfo[0]}</span>` : ''}${r.isExclusive ? `<span class="tag tag-exclusive">Exclusive product to ${escapeHtml(ctx.brandName)}</span>` : ''}</div>
            <span class="lender-name">${escapeHtml(r.lenderName || r.code)}</span>
            <span class="product-name">${escapeHtml(r.productName || 'Bridging finance product')} · ${chargeLabel.toLowerCase()}</span>
            ${(r.referralNotes || []).map((n) => `<span class="referral-note">${escapeHtml(n)}</span>`).join('')}
            ${badges.length > 0 ? `<div class="badges">${badges.map((t) => `<span class="tag tag-neutral">${escapeHtml(t)}</span>`).join('')}</div>` : ''}
          </div>
          <div class="cell cell-rate"><span class="cell-label">Rate pm</span><span class="cell-value rate-value">
            ${showDerivedRate ? '<span class="mini">approx</span>' : ''}${rateDisplay}${showDerivedRate ? `<span class="annual-line">${annualRateLine}</span>` : ''}
          </span></div>
          <div class="cell"><span class="cell-label">Monthly interest</span><span class="cell-value">${fromMini}${interestDisplay}${r.isRoa ? '' : nativeLine(loanGbp * r.rateMonthlyPct / 100, r.isRateRange && r.rateMonthlyPctTo !== null ? loanGbp * r.rateMonthlyPctTo / 100 : undefined)}</span></div>
          <div class="cell"><span class="cell-label">Max LTV</span><span class="cell-value">${r.maxLtvPct}%${r.combinedLtvPct != null ? `<span class="combined-ltv">Combined LTV ${r.combinedLtvPct.toFixed(1)}%</span>` : ''}</span></div>
          ${grossLoanCellHtml}
          <div class="cell"><span class="cell-label">Arrangement fee</span><span class="cell-value">${r.feePct > 0 ? r.feePct.toFixed(1) + '% · ' : ''}${money(r.feeAmountGbp)}${nativeLine(r.feeAmountGbp)}</span></div>
          <div class="cell cell-total"><span class="cell-label">Approx total cost</span><span class="cell-value">${fromMini}${costDisplay}${r.isRoa ? '' : nativeLine(r.totalCostGbp, r.isRateRange ? r.totalCostGbpTo : undefined)}</span></div>
          <div class="cell cell-action">
            <button type="button" class="btn ${isBest ? 'btn-primary' : 'btn-outline'}" data-apply="${code}" aria-label="Get exact terms from ${escapeHtml(r.lenderName || r.code)}">Get exact terms</button>
          </div>
        </div>
        <button type="button" class="details-toggle" data-toggle-details="${code}" aria-expanded="false" aria-controls="${detailsId}">Details ▾</button>
        <div class="popdown-wrap" id="${detailsId}">
          <div class="popdown-inner">
            <div class="details-grid">
              <div><span class="k">Exit fee</span>${r.exitFeePct > 0 ? r.exitFeePct.toFixed(1) + '% · ' : ''}${money(r.exitFeeAmountGbp)}${nativeLine(r.exitFeeAmountGbp)}</div>
              <div><span class="k">Administration fee</span>${money(r.adminFeeGbp)}${nativeLine(r.adminFeeGbp)}</div>
              ${isForeign ? `<div><span class="k">Lender's currency</span>${escapeHtml(r.currency)} · shown in £ at £1 = ${NATIVE_SYMBOLS[r.currency] || ''}${(r.fxRate as number).toFixed(4)}${r.fxRateDate ? ` (${new Date(r.fxRateDate + 'T12:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })})` : ''}</div>` : ''}
              <div><span class="k">Minimum interest period</span>${r.minInterestPeriodMonths > 0 ? r.minInterestPeriodMonths + ' months' : 'None'}</div>
              <div><span class="k">Charge type</span>${chargeLabel}</div>
              <div><span class="k">Regulation</span>${regulatedLabel}</div>
              ${r.features && r.features.length > 0 ? `<div><span class="k">Features</span>${r.features.map(escapeHtml).join(' · ')}</div>` : ''}
            </div>
            ${r.publicNotes ? `<div class="details-notes"><span class="k text-muted">Notes</span>${escapeHtml(r.publicNotes)}</div>` : ''}
          </div>
        </div>
      </li>`;
}
